import { unlockMobileScrolling, lockMobileScrolling } from '@archetype-themes/utils/a11y'
import { EVENTS } from '@archetype-themes/utils/events'
import { prepareTransition } from '@archetype-themes/utils/utils'

class CollectionMobileFilters extends HTMLElement {
  connectedCallback() {
    this.selectors = {
      filters: '.filter-wrapper',
      inlineWrapper: '#CollectionInlineFilterWrap',
      sortBtn: '.filter-sort'
    }
    this.config = {
      mobileFiltersInPlace: false,
      isOpen: false
    }

    this.mobileMediaQuery = window.matchMedia('(max-width: 768px)')
    this.handleMediaQueryChange = this.handleMediaQueryChange.bind(this)
    this.mobileMediaQuery.addListener(this.handleMediaQueryChange)
    this.handleMediaQueryChange(this.mobileMediaQuery)

    this.abortController = new AbortController()

    document.addEventListener(EVENTS.toggleMobileFilters, this.toggle.bind(this), {
      signal: this.abortController.signal
    })
    document.addEventListener('filter:selected', this.close.bind(this), {
      signal: this.abortController.signal
    })
  }

  disconnectedCallback() {
    this.abortController.abort()
  }

  /*===============================================
   ===========   MOBILE MODELL FILTER   ===========
   ================================================*/

  expandMobileModellIfNeeded() {
    const urlParams = new URLSearchParams(window.location.search)
    const isModellOpen = (urlParams.get('modell') === 'open')

    if (isModellOpen) {
      const button = document.querySelector('button[data-collapsible-id="filter-modell"]')
      const content = document.querySelector('div.collapsible-content--all[data-collapsible-id="filter-modell"]')
      if (button && content) {
        button.setAttribute('aria-expanded', 'true')
        button.classList.add('is-open')
        content.classList.add('is-open')
        content.style.height = 'auto'
        content.classList.remove('is-transitioning')
      }
    }
  }

  updateMobileActiveLink(newActiveLink) {
    const previousActiveLink = document.querySelector('.modell-nested-list__link.is-active')
    if (previousActiveLink) {
      previousActiveLink.classList.remove('is-active')
    }
    if (newActiveLink) {
      newActiveLink.classList.add('is-active')
    }
  }

  handleMobileToggleClick(e) {
    console.log('Mobile Toggle Click!')

    // Instead of listening at document level, we scope to the .filter-wrapper
    const filterWrapper = filters.querySelector('.filter-wrapper')
    
    const toggleButton = e.target.closest('.modell-nested-list__toggle-btn')
    if (!toggleButton) return

    const handle = toggleButton.getAttribute('data-toggle')
    console.log('Data toggle handle:', handle)
    const subMenu = filterWrapper.querySelector(`#modell-sub-${handle}`)
    console.log('Submenu element:', subMenu)
    if (!subMenu) return

    const isHidden = subMenu.classList.toggle('is-hidden')
    const parentItem = toggleButton.closest('.modell-nested-list__item')
    if (parentItem) {
      parentItem.classList.toggle('is-open', !isHidden)
    }

    toggleButton.setAttribute('aria-expanded', String(!isHidden))
    let parentContent = subMenu.closest('.collapsible-content--all')
    while (parentContent) {
      this.adjustMobileHeight(parentContent)
      parentContent = parentContent
        .closest('.collapsible-content--all')
        ?.parentElement
        ?.closest('.collapsible-content--all')
    }
  }

  handleMobileFilterClick(e) {
    const clickedLink = e.target.closest('.modell-nested-list__link')
    if (clickedLink) {
      this.updateMobileActiveLink(clickedLink)
    }
  }

  adjustMobileHeight(element) {
    if (!element) return
    element.style.height = 'auto'
    const newHeight = element.scrollHeight
    element.style.height = `${newHeight}px`
  }

  expandMobileActivePath() {
    const currentPath = window.location.pathname.replace(/\/$/, '')

    const links = document.querySelectorAll('.modell-nested-list__link')
    let activeLink = null

    links.forEach(link => {
      const linkUrl = new URL(link.href, window.location.origin)
      const linkPath = linkUrl.pathname.replace(/\/$/, '')
      if (linkPath === currentPath) {
        activeLink = link
      }
    })

    if (activeLink) {
      activeLink.classList.add('is-active')
      let parentItem = activeLink.closest('.modell-nested-list__item')
      while (parentItem) {
        const childUl = parentItem.querySelector('.modell-nested-list--child')
        if (childUl) childUl.classList.remove('is-hidden')

        parentItem.classList.add('is-open')
        const toggleBtn = parentItem.querySelector('.modell-nested-list__toggle-btn')
        if (toggleBtn) {
          toggleBtn.setAttribute('aria-expanded', 'true')
        }
        parentItem = parentItem.parentElement?.closest('.modell-nested-list__item')
      }
    }
  }

  initMobileModellMenu() {
    // Expand any active path or URL param
    this.expandMobileActivePath()
    this.expandMobileModellIfNeeded()

    // Bind `this` for these event listeners
    document.addEventListener('click', this.handleMobileToggleClick.bind(this), {
      signal: this.abortController.signal
    })
    document.addEventListener('click', this.handleMobileFilterClick.bind(this), {
      signal: this.abortController.signal
    })
    document.addEventListener('click', (e) => {
      console.log('[DEBUG] Mobile Document click fired on:', e.target)
    })
  }

  /*===============================================
   ===========  END MOBILE MODELL FILTER  =========
   ================================================*/

  async renderFiltersOnMobile() {
    if (this.config.mobileFiltersInPlace) {
      return
    }

    const filters = await this.getFilters()
    const inlineWrapper = this.querySelector(this.selectors.inlineWrapper)

    inlineWrapper.innerHTML = ''
    inlineWrapper.append(filters)

    // Re-init your custom code
    this.expandMobileActivePath()
    this.expandMobileModellIfNeeded()

    // Instead of listening at document level, we scope to the .filter-wrapper
    const filterWrapper = filters.querySelector('.filter-wrapper')
    if (filterWrapper) {
      filterWrapper.addEventListener('click', this.handleMobileToggleClick.bind(this), {
        signal: this.abortController.signal
      })
      filterWrapper.addEventListener('click', this.handleMobileFilterClick.bind(this), {
        signal: this.abortController.signal
      })
    }

    this.sortBtns = this.querySelectorAll(this.selectors.sortBtn)
    if (this.sortBtns.length) {
      this.sortBtns.forEach((btn) => {
        btn.addEventListener('click', this.handleSortButtonClick.bind(this), {
          signal: this.abortController.signal
        })
      })
    }

    this.config.mobileFiltersInPlace = true
  }

  handleSortButtonClick(evt) {
    const btn = evt.currentTarget
    this.close()
    const sortValue = btn.dataset.value
    this.dispatchEvent(new CustomEvent(EVENTS.sortSelected, { detail: { sortValue }, bubbles: true }))
  }

  handleMediaQueryChange(mql) {
    if (mql.matches) {
      // Wait for ajax-renderer.js to adjust URL parameters, then fetch filters
      setTimeout(() => {
        this.renderFiltersOnMobile()
      }, 100)
    }
  }

  /*============================================================================
    Open and close filter drawer
  ==============================================================================*/
  toggle() {
    if (this.config.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    const filters = this.querySelector(this.selectors.filters)

    prepareTransition(filters, () => filters.classList.add('is-active'))

    this.config.isOpen = true
    lockMobileScrolling()

    // Bind the keyup event handler
    this._keyupHandler = (evt) => {
      if (evt.keyCode === 27) {
        this.close()
      }
    }
    window.addEventListener('keyup', this._keyupHandler, {
      signal: this.abortController.signal
    })
  }

  close() {
    const filters = this.querySelector(this.selectors.filters)

    prepareTransition(filters, () => filters.classList.remove('is-active'))

    this.config.isOpen = false
    unlockMobileScrolling()

    // Remove the keyup event handler
    window.removeEventListener('keyup', this._keyupHandler)
  }

  async getFilters() {
    const searchParams = window.location.search.slice(1)
    const url = `${window.location.pathname}?section_id=item-grid-filters&${searchParams}`
    const response = await fetch(url)
    if (!response.ok) {
      throw response
    }

    const responseText = await response.text()
    const html = new DOMParser().parseFromString(responseText, 'text/html')
    return html.querySelector(this.selectors.filters)
  }
}

customElements.define('collection-mobile-filters', CollectionMobileFilters)
